/**
 * Presentation metadata for each brand — the layer `src/config/brands.ts` deliberately
 * left out until a branded screen existed. One now does (the parent brand profile
 * switcher), so the display names, logos and accents live here rather than being guessed
 * at call sites.
 *
 * `brands.ts` stays what it is: the single TypeScript mirror of the `programs_brand_key_check`
 * CHECK constraint. This file only describes how each of those keys *looks*, and adds no new
 * keys of its own — `BRAND_PROFILES` is keyed by `BrandKey`, so a brand added to the CHECK
 * without a profile is a type error rather than a blank header.
 *
 * **Still not a security boundary.** Nothing here is consulted by RLS. A brand profile decides
 * which catalog a parent is *looking at*, never what they are allowed to read. See
 * `docs/implementation_plans/kaleido/infrastructure-plan.md`.
 */

import { BRAND_KEYS, DEFAULT_BRAND_KEY, isBrandKey, type BrandKey } from './brands';
import type { LabelKey } from './labels';
import { getLogoPath } from '../utils/theme';

/** How a brand's program catalog can be browsed. */
export type CatalogView = 'list' | 'calendar';

export interface BrandProfile {
    readonly key: BrandKey;
    /** Full legal-ish name, for footers and the switcher's secondary line. */
    readonly name: string;
    /** What the header says. */
    readonly shortName: string;
    /**
     * One line under the name, in the switcher and on the sign-up picker.
     *
     * Says what the brand IS, not which formats it happens to sell. Kaleido's first said
     * "Workshops, series and community events", which described this term's catalog and would
     * have read as the brand's definition — it is an early-education programme for ages 2–5
     * that runs those formats, not a workshop series.
     */
    readonly tagline: string;
    /**
     * Square logo in `public/`, or null when the brand has no artwork and shows its name alone.
     *
     * There is deliberately no emoji stand-in. Brands had one (🏫, 🌈) for the badge pill, where
     * a logo is too small to read, but neither appeared in either logo, and next to a program's
     * own emoji it read as a second program icon rather than as a brand. The pill's color and
     * name say which brand it is.
     */
    readonly logoPath: string | null;
    /**
     * Logo for print — the invoice letterhead. BHCS has a smaller asset than the header uses;
     * a brand without one falls back to `logoPath`.
     */
    readonly printLogoPath?: string;
    /** Primary accent — headers, active nav, primary buttons. */
    readonly accent: string;
    /** A tint of `accent` light enough to sit text on. */
    readonly accentSoft: string;
    /** Text/border color that reads against `accentSoft`. */
    readonly accentInk: string;
    /**
     * Whether this brand's surfaces carry the bilingual (中文 + English) treatment.
     *
     * BHCS does; Kaleido is English-only. Without this, `useProgramTypeName` prefixes every
     * Kaleido section with the decorative CJK glyph its type metadata carries, and an
     * English-only brand grows a Chinese label it never asked for.
     */
    readonly bilingual: boolean;
    /**
     * Whether this brand sells anything on a recurring schedule.
     *
     * BHCS after-school bills monthly; Kaleido sells fixed-term series, one-off workshops and
     * free events, so nothing recurs. Drives whether autopay is worth offering as a shortcut —
     * the setting itself is per family and stays reachable from Account either way.
     */
    readonly billsRecurring: boolean;
    /** Catalog views this brand offers, in tab order. */
    readonly views: readonly CatalogView[];
    /**
     * Section order for this brand's catalog, by `programs.type`. `null` keeps the historical
     * BHCS behaviour (camp first, then alphabetical) rather than freezing an order that has
     * never been written down.
     */
    readonly programTypeOrder: readonly string[] | null;
    /** What this brand asks a family when they sign up and add a child. */
    readonly intake: BrandIntake;
    /**
     * The starter questions the Assistant offers, in order. Each is sent as the question
     * itself, so it has to be one the assistant can answer for this brand's families: BHCS's
     * are about monthly payments and BHCS Cash, which a Kaleido family buying a workshop has
     * no reason to ask.
     */
    readonly assistantStarters: readonly LabelKey[];
}

/** One "interested programs" checkbox on the add-child form. */
export interface ProgramInterest {
    /** Stored verbatim in `students.metadata.interested_programs`. */
    readonly value: string;
    readonly label: string;
    readonly icon: string;
}

/**
 * The questions a brand's sign-up asks.
 *
 * Declared per brand rather than branched on at the call sites, because the two forms differ
 * in four unrelated places and `activeBrand === 'kaleido'` scattered through a 600-line form
 * is how the two brands quietly drift apart.
 */
export interface BrandIntake {
    /** Onboarding headline and blurb — the first thing a new family reads. */
    readonly welcomeTitle: string;
    readonly welcomeBlurb: string;
    /**
     * Whether the add-child form asks for school and grade, and requires them.
     *
     * A BHCS after-school day is organized by both. Kaleido serves ages 2–5, where grade is
     * not a thing a family can answer — and `students.metadata.grade` is read only by
     * after-school surfaces, so a Kaleido child having none costs nothing.
     */
    readonly collectsSchoolAndGrade: boolean;
    /** Whether it asks for a classroom room number, which exists to group BHCS pickup. */
    readonly collectsRoomNumber: boolean;
    /** The interest checkboxes offered, in display order. */
    readonly interests: readonly ProgramInterest[];
    /**
     * Why this brand asks for a home address and ZIP, shown next to the fields. `null` means
     * the brand does not ask.
     *
     * The only reason to ask is a resident discount, and that is Kaleido's. A BHCS family is not
     * asked, at sign-up or on the Account page. Asking for a home address with no stated reason
     * is exactly what makes a parent skip it, or stop trusting the form. An address already
     * on file still shows on either profile, since it belongs to the account.
     */
    readonly addressPrompt: string | null;
}

/**
 * The legal entity behind every brand in this portal.
 *
 * BHCS and Kaleido are both programs of Bright Minds Collective LLC — neither is the company.
 * This is the name that belongs on a tax document, and it is the same one whatever brand sold
 * the class, which is why it is a constant here and not a field on `BrandProfile`.
 */
export const LEGAL_ENTITY_NAME = 'Bright Minds Collective LLC';

export const BRAND_PROFILES: Record<BrandKey, BrandProfile> = {
    bhcs: {
        key: 'bhcs',
        name: 'Bright Horizon Chinese School',
        shortName: 'BHCS',
        tagline: 'After-school, enrichment and camps',
        logoPath: '/assets/bhcs-logo.png',
        printLogoPath: '/assets/bhcs-logo-small.png',
        accent: '#2563eb',
        accentSoft: '#eff6ff',
        accentInk: '#1d4ed8',
        bilingual: true,
        billsRecurring: true,
        views: ['list'],
        programTypeOrder: null,
        intake: {
            welcomeTitle: 'Welcome to the BHCS Family!',
            welcomeBlurb:
                "Let's get you all set up so your little ones can join our awesome adventures! " +
                'This will only take a minute - promise! ⚡',
            collectsSchoolAndGrade: true,
            collectsRoomNumber: true,
            // The values BHCS has always stored. `camps` is plural and does not match the
            // `camp` program type; that predates this file and is left alone rather than
            // migrated under cover of a refactor.
            interests: [
                { value: 'extracurricular', label: 'Enrichment Programs', icon: '🎨' },
                { value: 'after-school', label: 'After School Programs', icon: '🧸' },
                { value: 'camps', label: 'Camps', icon: '🏕️' },
            ],
            // No resident pricing, so no reason to ask.
            addressPrompt: null,
        },
        // What BHCS parents actually asked: their invoice, camps and classes to sign up for,
        // their Flex days, and where a class meets.
        assistantStarters: [
            'genie.chip.explainInvoices',
            'genie.chip.signUp',
            'genie.chip.flexDays',
            'genie.chip.nextClassWhere',
        ],
    },
    kaleido: {
        key: 'kaleido',
        name: 'Kaleido',
        shortName: 'Kaleido',
        tagline: 'Early learning for ages 2–5',
        // PNG, not webp — used inline in emails and printed invoices as well as the browser
        // header, and webp support isn't universal in those contexts (older Outlook builds,
        // some PDF/print pipelines). Matches BHCS's own logo, which is PNG for the same reason.
        logoPath: '/assets/kaleido-logo.png',
        printLogoPath: '/assets/kaleido-logo-small.png',
        // Taken from the purple in the logo's own wordmark rather than from Tailwind's violet
        // scale, which is where these three started and which appears nowhere in the artwork.
        // The logo's purple is #ad7fc9, too light to carry white text — it fails the contrast
        // check at 3.2:1 — so `accent` is that hue darkened to 5.7:1, and `accentInk`, used for
        // hover and for text on `accentSoft`, is one step darker again.
        accent: '#7e52ab',
        accentSoft: '#f4eef9',
        accentInk: '#6a4394',
        bilingual: false,
        billsRecurring: false,
        views: ['calendar', 'list'],
        programTypeOrder: [
            'kaleido-enrichment',
            'kaleido-workshop',
            'kaleido-readiness',
            'kaleido-community',
        ],
        intake: {
            welcomeTitle: 'Welcome to Kaleido!',
            // Leads with what Kaleido IS before what it runs. This is the first sentence a new
            // family reads, and listing the formats alone reads as the brand's definition
            // (BHCS-130) — the same complaint as the tagline, in the place it costs most.
            welcomeBlurb:
                'Kaleido is early learning for ages 2–5. ' +
                "Let's get you set up so your child can join a workshop, a series or a free " +
                'community event. It only takes a minute. ⚡',
            // Ages 2–5: most have not started school, and none of them have a grade.
            collectsSchoolAndGrade: false,
            // Room numbers group BHCS pickup. Kaleido runs at its own site and has none.
            collectsRoomNumber: false,
            // The four catalog categories verbatim, so an interest can be matched straight
            // against `programs.type` when recommendations arrive.
            interests: [
                { value: 'kaleido-enrichment', label: 'Enrichment Series', icon: '🎨' },
                { value: 'kaleido-workshop', label: 'Specialty Workshops', icon: '🧩' },
                { value: 'kaleido-readiness', label: 'School Readiness', icon: '🔤' },
                { value: 'kaleido-community', label: 'Community Events', icon: '🎈' },
            ],
            // "Select" programs: only those carrying the Foster City Resident line item. "Before
            // you enroll": eligibility is read when the enrollment's invoice is created, so a ZIP
            // added afterwards does not change an invoice already issued.
            addressPrompt:
                'Live in Foster City? Residents get a discount on select Kaleido programs. ' +
                "Add your address and ZIP code before you enroll and it's applied automatically.",
        },
        // A new family's questions first: what is on, and what suits a child of this age.
        assistantStarters: [
            'genie.chip.comingUp',
            'genie.chip.programsForAge',
            'genie.chip.nextClass',
            'genie.chip.explainInvoices',
        ],
    },
};

/**
 * The order the sign-up picker offers brands in, and which one it starts on.
 *
 * Kaleido first because most new families arriving at sign-up are Kaleido families, and
 * first is also where sign-up starts: the picker selects `SIGNUP_BRAND_ORDER[0]`. This is a
 * marketing fact about new customers, so it lives here and **not** in `DEFAULT_BRAND_KEY`:
 * that constant is the fallback for everyone, including the admin and teacher portals which
 * render outside `BrandProfileProvider`, and flipping it would silently make those surfaces
 * English-only and re-theme them. Existing families are unaffected either way — their profile
 * is derived from their enrollments or from a choice they already made.
 *
 * Declared rather than reordering `BRAND_KEYS`: that array mirrors the CHECK constraint, and
 * its order should answer to the database, not to where the next customer is coming from.
 */
export const SIGNUP_BRAND_ORDER: readonly BrandKey[] = ['kaleido', 'bhcs'];

/** Every profile, in `BRAND_KEYS` order. */
export const BRAND_PROFILE_LIST: readonly BrandProfile[] = BRAND_KEYS.map(k => BRAND_PROFILES[k]);

/**
 * The profile for a brand key, falling back to the default brand for anything unrecognized.
 * Callers hand this raw `programs.brand_key` values and stored preferences, so it must not
 * throw on a value written by an older build.
 */
export function brandProfileOf(value: unknown): BrandProfile {
    return BRAND_PROFILES[isBrandKey(value) ? value : DEFAULT_BRAND_KEY];
}

/**
 * The logo to render for a brand inside the parent shell, or null when it has no artwork and
 * the caller should show the brand's name alone.
 *
 * Owns one rule that is easy to get wrong at a call site: **the seasonal override belongs to
 * the default brand alone.** `getLogoPath()` swaps in the 福 logo for Lunar New Year, which is
 * a BHCS tradition — rendering it on another brand's header puts someone else's holiday on
 * their masthead.
 *
 * Scope is deliberately the parent shell. The portal's own identity — tab title, favicon, PWA
 * icon, the login and legal pages, the admin and teacher shells — stays BHCS, because it is
 * one app on one domain and a family installs "My BHCS Portal" whichever catalog they browse.
 * Those surfaces also render outside `BrandProfileProvider`, so there is no active brand to
 * ask. Making them per-brand would be wrong rather than missing.
 */
export function brandLogoPath(brand: BrandKey): string | null {
    if (brand === DEFAULT_BRAND_KEY) return getLogoPath();
    return BRAND_PROFILES[brand].logoPath;
}

/** The logo an invoice or other printed document should carry for a brand. */
export function brandPrintLogoPath(brand: unknown): string | null {
    const profile = brandProfileOf(brand);
    return profile.printLogoPath ?? profile.logoPath;
}

/** Whether `view` is one this brand offers; used to reject a stale stored preference. */
export function brandSupportsView(brand: BrandKey, view: CatalogView): boolean {
    return BRAND_PROFILES[brand].views.includes(view);
}

/** The view a brand's catalog opens on. */
export function defaultViewFor(brand: BrandKey): CatalogView {
    return BRAND_PROFILES[brand].views[0];
}
